import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";
import { useTransition, useSpring, useChain, animated } from "react-spring";
import { RefreshRounded } from "@mui/icons-material";

import { Caret } from "commons";

import { getErrorStyle, ERROR } from "../helpers/ErrorHelpers";

const DISABLED_OPACITY = 0.6;

export function Message({
    severity = ERROR,
    shown = true,
    sideMargins = false,
    text = "",
    marginTop,
    errorCode,
    action = null
}) {
    const [open, setOpen] = useState(true);
    const [elementHeight, setElementHeight] = useState(0);

    const msgList = useRef();

    function handleResize() {
        const defaultHeight = 120;
        open && msgList.current && setElementHeight(msgList.current.clientHeight || defaultHeight);
    }

    useEffect(() => {
        handleResize();

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
        // eslint-disable-next-line
    }, []);

    // eslint-disable-next-line
    useEffect(() => {
        if (open && msgList.current && msgList.current.clientHeight) {
            setElementHeight(msgList.current.clientHeight);
        }
    });

    const toggleMessage = () => setOpen(v => !v);

    const messages = text && text.map ? text.map(msg => msg) : [text];

    const content = [
        ...messages,
        errorCode && <span key="error-code">Error code: {errorCode}</span>,
        action && (
            <Button
                key="retry-button"
                data-testid="action-button"
                onClick={() => action.retry()}
                disabled={action.isLoading}
            >
                <Spin isLoading={action.isLoading}>
                    <RefreshRounded fontSize="small" aria-label="Loading" />
                </Spin>
                <ActionText>Try again</ActionText>
            </Button>
        )
    ].filter(Boolean);

    const collapsedText = () => {
        if (severity === ERROR) {
            return errorCode ? `Error code: ${errorCode}` : "Error";
        }

        return "Important information";
    };

    const containerRef = useRef();
    const closedHeight = 38;
    const { height, ...rest } = useSpring({
        ref: containerRef,
        config: { tension: 300 },
        to: { height: open ? elementHeight : closedHeight }
    });

    const expandRef = useRef();
    const expandTransition = useTransition(!open, null, {
        ref: expandRef,
        config: { duration: 150 },
        from: { opacity: 0, transform: "scaleY(0) translateX(-30px)" },
        enter: { opacity: 1, transform: "scaleY(1) translateX(0px)" },
        leave: { opacity: 0, transform: "scaleY(0) translateX(-30px)" }
    });

    const contentRef = useRef();
    const contentTransition = useTransition(
        open ? content : [],
        item => (item && item.key) || item,
        {
            ref: contentRef,
            unique: true,
            config: { tension: 310, friction: 30 },
            from: { opacity: 0, transform: "scaleY(0) translateX(-30px)" },
            enter: { opacity: 1, transform: "scaleY(1) translateX(0px)" },
            leave: { opacity: 0, transform: "scaleY(0) translateX(-30px)" }
        }
    );

    const contentOpenDelay = 0.15;

    useChain(
        open ? [containerRef, contentRef, expandRef] : [expandRef, contentRef, containerRef],
        open ? [0, contentOpenDelay, 0] : [0, 0, 0]
    );

    if (!shown) {
        return null;
    }

    const Icon = getErrorStyle(severity).icon;

    return (
        <OuterContainer
            data-testid="message-component"
            sideMargins={sideMargins}
            marginTop={marginTop}
            shown={shown}
        >
            <InnerContainer
                data-testid="content-container"
                color={getErrorStyle(severity).color}
                style={{ height, ...rest }}
            >
                <MsgIcon>
                    <Icon fontSize="small" />
                </MsgIcon>
                <MsgList ref={msgList}>
                    {contentTransition.map(({ item, key, props }) => (
                        <Item key={key} style={{ ...props }} data-testid="message-row">
                            {item}
                        </Item>
                    ))}
                    {expandTransition.map(
                        ({ item, key, props }) =>
                            item && (
                                <ExpandText
                                    key={key}
                                    data-testid="expand-text"
                                    style={props}
                                    onClick={toggleMessage}
                                >
                                    {collapsedText()}
                                </ExpandText>
                            )
                    )}
                </MsgList>
                <CloseIcon data-testid="close-icon" onClick={toggleMessage}>
                    <Caret direction={open ? "up" : "down"} />
                </CloseIcon>
            </InnerContainer>
        </OuterContainer>
    );
}

const OuterContainer = styled.div`
    margin-top: ${props => props.marginTop || "0px"};
    padding: 0;

    @media (min-width: 960px) {
        margin-left: ${props => (props.sideMargins ? "1%" : "none")};
        margin-right: ${props => (props.sideMargins ? "1%" : "none")};
    }

    @media print {
        display: none;
    }
`;

const InnerContainer = styled(animated.div)`
    display: flex;
    flex: 1;
    align-items: center;
    text-align: left;
    font-family: vag-rounded-light, Helvetica, Arial, sans-serif;
    background-color: #fff;
    border: 1px solid ${props => props.color};
    color: ${props => props.color};
    margin: 0;
`;

const MsgIcon = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    padding: 10px;
`;

const Item = styled(animated.div)`
    will-change: transform, opacity;
    margin-bottom: 10px;

    p {
        font-size: 0.875rem;
    }
`;

const MsgList = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    font-size: 0.875rem;
    padding: 10px 0;
    margin: 0;

    div:last-child,
    p:last-child {
        margin: 0;
    }
`;

const ExpandText = styled(animated.div)`
    position: absolute;
    top: 2px;
    left: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    will-change: transform, opacity;
    cursor: pointer;
`;

const CloseIcon = styled.div`
    width: 38px;
    height: 38px;
    align-self: flex-end;
    display: flex;
    justify-content: center;
    align-items: center;

    &:active {
        opacity: 0.5;
    }
`;

const Button = styled.button`
    background: none;
    outline: none;
    cursor: ${props => (props.disabled ? "not-allowed" : "pointer")};
    opacity: ${props => (props.disabled ? DISABLED_OPACITY : 1)};
    color: inherit;
    font-size: inherit;
    text-decoration: underline;
    font-family: vag-rounded-light, Helvetica, Arial, sans-serif;
    border: none;
    padding: 0;
    display: flex;
    align-items: center;
`;

const ActionText = styled.span`
    padding-left: 10px;
`;

const rotation = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const Spin = styled.div`
    display: inline-block;
    height: 20px;
    cursor: ${props => (props.isLoading ? "not-allowed" : "pointer")};
    animation: ${props => (props.isLoading ? rotation : "none")} 3s linear infinite;

    @media (prefers-reduced-motion) {
        animation: none;
    }
`;

Message.propTypes = {
    severity: PropTypes.string,
    className: PropTypes.string,
    fontSize: PropTypes.string,
    padding: PropTypes.string,
    marginTop: PropTypes.string,
    sideMargins: PropTypes.bool,
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]),
    shown: PropTypes.bool,
    errorCode: PropTypes.string,
    action: PropTypes.shape({
        isLoading: PropTypes.bool,
        retry: PropTypes.func
    })
};
